import { useEffect, useState } from 'react';
import { Phone, MapPin, ArrowDownRight, ShieldCheck, Sparkles, Star } from 'lucide-react';
import { LANDING } from '@/constants/testIds';
import { BUSINESS } from '@/data/business';
import { BOUTIQUE_PHOTOS } from '@/data/photos';

/**
 * Hero with cross-fade slideshow of real boutique photos.
 */
export const Hero = () => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setIndex((i) => (i + 1) % BOUTIQUE_PHOTOS.length);
        }, 5200);
        return () => clearInterval(id);
    }, []);

    return (
        <section
            id="hero"
            data-testid={LANDING.hero}
            className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-24 overflow-hidden grain min-h-[100svh] flex flex-col justify-end"
        >
            {/* Background slideshow */}
            <div className="absolute inset-0 z-0">
                {BOUTIQUE_PHOTOS.map((p, i) => (
                    <img
                        key={p.src}
                        src={p.src}
                        alt={p.alt}
                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-in-out"
                        style={{
                            opacity: i === index ? 1 : 0,
                            animation: i === index ? 'heroZoom 7s ease-out forwards' : undefined,
                        }}
                        loading={i === 0 ? 'eager' : 'lazy'}
                    />
                ))}

                {/* Strong cinematic overlay: image visible right, dark left for H1 legibility */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/97 via-[#0A0A0A]/88 to-[#0A0A0A]/50" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-[#0A0A0A]/30" />
                <div className="absolute inset-0 bg-grid opacity-35" />
            </div>

            <style>{`
                @keyframes heroZoom {
                    0% { transform: scale(1.04); }
                    100% { transform: scale(1.12); }
                }
            `}</style>

            {/* Blur orbs */}
            <div className="absolute top-1/3 -left-32 h-[420px] w-[420px] blur-orb-yellow z-0 opacity-70" />
            <div className="absolute bottom-0 right-1/3 h-[420px] w-[420px] blur-orb-orange z-0 opacity-60" />

            {/* Hazard stripe on the right edge */}
            <div className="hidden lg:block absolute top-32 bottom-12 right-0 w-2 hazard-stripes-thin opacity-80 z-10" />

            <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 w-full">
                {/* Kicker row */}
                <div className="flex flex-wrap items-center gap-3 mb-6 sm:mb-8 reveal in-view">
                    <div className="inline-flex items-center gap-2 glass-yellow px-3 py-1.5 rounded-sm">
                        <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#FFD500]" />
                        <span className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#FFD500] font-bold">
                            Boutique ouverte · 37 av. Ch. de Gaulle · {BUSINESS.city}
                        </span>
                    </div>
                    <a
                        href={BUSINESS.googleReviewsSearchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 glass px-3 py-1.5 rounded-sm text-[10px] sm:text-xs uppercase tracking-[0.22em] text-white/80 font-semibold hover:text-[#FFD500] transition-colors"
                    >
                        <Star className="h-3 w-3 fill-[#FFD500] text-[#FFD500]" />
                        Avis Google
                    </a>
                </div>

                {/* H1 — SEO optimized */}
                <h1
                    data-testid={LANDING.heroTitle}
                    className="font-display uppercase leading-[0.86] text-white reveal in-view max-w-[1100px] drop-shadow-[0_4px_22px_rgba(0,0,0,0.7)]"
                    style={{ animationDelay: '0.1s' }}
                >
                    <span className="block text-5xl sm:text-7xl lg:text-[7.6vw] xl:text-[7rem]">
                        Vêtements de travail
                    </span>
                    <span className="block text-5xl sm:text-7xl lg:text-[7.6vw] xl:text-[7rem] text-outline-yellow-thick">
                        & EPI Pros
                    </span>
                    <span className="block text-3xl sm:text-5xl lg:text-[4.6vw] xl:text-[4.2rem] mt-2 text-white">
                        à <span className="text-[#FFD500]">Aulnay-sous-Bois</span>
                        <span className="inline-block ml-3 sm:ml-4 align-middle h-1.5 w-12 sm:w-20 bg-[#FFD500]" />
                    </span>
                </h1>

                {/* Sub + CTAs + stats */}
                <div className="mt-8 sm:mt-12 grid lg:grid-cols-[1.15fr,1fr] gap-8 lg:gap-16 items-end">
                    <div className="reveal in-view" style={{ animationDelay: '0.25s' }}>
                        <p className="text-lg sm:text-xl text-white max-w-2xl leading-relaxed">
                            Boutique d'<strong className="text-white">équipement de protection individuelle</strong> et de vêtements professionnels à <strong className="text-[#FFD500]">Aulnay-sous-Bois (93)</strong>. 20 ans d'expertise au service des pros du <strong className="text-white">BTP</strong>, de l'aéroportuaire, du médical, de l'industrie et de la logistique — du petit artisan au grand groupe.
                        </p>

                        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
                            <a
                                data-testid={LANDING.heroCallBtn}
                                href={`tel:${BUSINESS.phoneTel}`}
                                className="group inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#FFD500] text-black font-bold uppercase tracking-wider text-sm hover:bg-white transition-all corner-cut shadow-[0_8px_30px_-8px_rgba(255,213,0,0.4)]"
                            >
                                <Phone className="h-5 w-5" />
                                <span>Appeler · {BUSINESS.phone}</span>
                                <ArrowDownRight className="h-4 w-4 -rotate-90 group-hover:translate-x-1 transition-transform" />
                            </a>
                            <a
                                data-testid={LANDING.heroDirectionsBtn}
                                href={BUSINESS.googleMapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-3 px-6 py-4 bg-black/60 backdrop-blur border border-white/40 text-white font-bold uppercase tracking-wider text-sm hover:border-[#FFD500] hover:text-[#FFD500] transition-colors corner-cut"
                            >
                                <MapPin className="h-5 w-5" />
                                <span>Itinéraire boutique</span>
                            </a>
                        </div>

                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white">
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4 text-[#FFD500]" /> EPI normés CE
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-[#FFD500]" /> Flocage & broderie sur place
                            </span>
                            <span className="inline-flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-[#FFD500]" /> Livraison Île-de-France
                            </span>
                        </div>
                    </div>

                    {/* Stats bento — 3 items */}
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 reveal in-view" style={{ animationDelay: '0.4s' }}>
                        {[
                            { value: '20', unit: 'ans', label: "d'expertise EPI à Aulnay" },
                            { value: '3K+', unit: '', label: 'pros et entreprises équipés' },
                            { value: '93', unit: '600', label: 'Aulnay-sous-Bois · IDF' },
                        ].map((s, i) => (
                            <div key={i} className="glass corner-cut p-4 sm:p-5">
                                <div className="font-display text-3xl sm:text-5xl text-[#FFD500] leading-none">
                                    {s.value}
                                    {s.unit && (
                                        <span className="ml-1 text-base sm:text-lg text-white/85 align-baseline">
                                            {s.unit}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 text-xs sm:text-sm uppercase tracking-[0.16em] text-white font-medium">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Slideshow indicator */}
                <div className="absolute bottom-6 right-6 sm:right-8 hidden md:flex items-center gap-2 z-20">
                    {BOUTIQUE_PHOTOS.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            aria-label={`Photo ${i + 1}`}
                            onClick={() => setIndex(i)}
                            className={`h-1.5 transition-all duration-500 ${
                                i === index ? 'w-8 bg-[#FFD500]' : 'w-3 bg-white/30 hover:bg-white/60'
                            }`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
